// Pure logic behind Ask Masira's spoken replies (the browser speechSynthesis wrapper is hooks/useSpeech.ts).

export interface VoiceLike { lang: string; localService: boolean; name: string }

export const MAX_CHUNK = 200;
export const AUTO_SPEAK_KEY = 'masira.chat.autoSpeak';

const END_PUNCT = /[.!?:;…؟۔،؛]$/;

/** Reduces a displayed assistant reply (markdown, citations) to plain text a voice can read, one pause per line. */
export function toSpeakableText(raw: string): string {
  const lines = raw
    .replace(/```[\s\S]*?(```|$)/g, '\n')
    .replace(/\s*\(Source:[^)]*\)/gi, '')
    .replace(/[⁦-⁩]/g, '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\s*https?:\/\/\S+/gi, '')
    .split('\n');
  const out: string[] = [];
  for (const line of lines) {
    let s = line.trim();
    if (!s || s.startsWith('|') || /^[-*_]{3,}$/.test(s)) continue;
    s = s
      .replace(/^#{1,6}\s+/, '')
      .replace(/^>\s?/, '')
      .replace(/^[-*+•]\s+/, '')
      .replace(/(\*\*|__)(.+?)\1/g, '$2')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/`/g, '')
      .trim();
    if (!s) continue;
    out.push(END_PUNCT.test(s) ? s : `${s}.`);
  }
  return out.join(' ');
}

function splitLong(s: string, max: number): string[] {
  if (s.length <= max) return [s];
  const parts: string[] = [];
  let rest = s;
  while (rest.length > max) {
    const window = rest.slice(0, max);
    let cut = Math.max(window.lastIndexOf(', '), window.lastIndexOf('; '), window.lastIndexOf('، '), window.lastIndexOf(': '));
    if (cut < max * 0.4) cut = window.lastIndexOf(' ');
    if (cut <= 0) cut = max - 1;
    parts.push(rest.slice(0, cut + 1).trim());
    rest = rest.slice(cut + 1).trim();
  }
  if (rest) parts.push(rest);
  return parts.filter(Boolean);
}

/** Splits text into utterance-sized chunks (<= max chars) on sentence ends, then clauses, then spaces. */
export function chunkForSpeech(text: string, max = MAX_CHUNK): string[] {
  const sentences = text.match(/[\s\S]+?(?:[.!?؟۔]+["')\]]*(?=\s|$)|$)/g) ?? [];
  const chunks: string[] = [];
  let cur = '';
  for (const raw of sentences) {
    const s = raw.trim();
    if (!s) continue;
    for (const piece of splitLong(s, max)) {
      if (cur && cur.length + 1 + piece.length > max) {
        chunks.push(cur);
        cur = piece;
      } else {
        cur = cur ? `${cur} ${piece}` : piece;
      }
    }
  }
  if (cur) chunks.push(cur);
  return chunks;
}

const LATIN_UI = ['en', 'fr', 'es', 'de'];

/** Language to speak in: Arabic script wins; Latin text follows a Latin-script UI language, else English. */
export function detectSpeechLanguage(text: string, uiLanguage: string): string {
  const letters = text.match(/\p{L}/gu) ?? [];
  const arabic = text.match(/[؀-ۿݐ-ݿ]/g) ?? [];
  if (letters.length > 0 && arabic.length / letters.length >= 0.3) return 'ar';
  return LATIN_UI.includes(uiLanguage) ? uiLanguage : 'en';
}

const primary = (lang: string) => lang.toLowerCase().replace('_', '-').split('-')[0];

/** An on-device voice for the language, or null so the caller can say there is none. Network voices are never used: reply text must not leave the device. */
export function pickVoice<T extends VoiceLike>(voices: T[], language: string): T | null {
  const want = primary(language);
  return voices.find((v) => v.localService && primary(v.lang) === want) ?? null;
}

/** After any cloud failure, wait this long before trying the cloud voice again. */
export const CLOUD_PAUSE_MS = 60_000;

/** Cloud voice only for a language the provider covers and only outside a post-failure pause; otherwise the on-device voice. */
export function chooseEngine(o: { cloudLanguages: string[]; language: string; pausedUntil: number; now: number }): 'cloud' | 'device' {
  if (o.now < o.pausedUntil) return 'device';
  return o.cloudLanguages.includes(primary(o.language)) ? 'cloud' : 'device';
}

export function readAutoSpeak(storage: Pick<Storage, 'getItem'> | null): boolean {
  try { return storage?.getItem(AUTO_SPEAK_KEY) === '1'; } catch { return false; }
}

export function writeAutoSpeak(storage: Pick<Storage, 'setItem'> | null, on: boolean): void {
  try { storage?.setItem(AUTO_SPEAK_KEY, on ? '1' : '0'); } catch { /* storage blocked: the preference just does not persist */ }
}
